import type { User } from '@/types/user'
import type { UserStatus } from '@/types/user'
import type { UserRole } from '@/types/user-role'

// Map internal roles to display names
export const roleNames: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  gym_manager: 'Gym Manager',
  assistant: 'Assistant',
  coach: 'Coach',
  member: 'Member',
  guest: 'Guest',
}

// User statuses that should block access to the application
const RESTRICTED_STATUSES: UserStatus[] = ['banned', 'suspended', 'deleted']

// Check if user has a restricted status (banned, suspended, deleted)
export function isUserRestricted(user: User | null | undefined): boolean {
  if (!user) return true
  return RESTRICTED_STATUSES.includes(user.status)
}

// Check if user has active access to the application
export function hasActiveAccess(user: User | null | undefined): boolean {
  if (!user) return false
  return !RESTRICTED_STATUSES.includes(user.status)
}

// Returns true if user has any of the specified roles
export function hasRole(user: User | null | undefined, roles: UserRole | UserRole[]) {
  if (!user) return false

  const rolesToCheck = Array.isArray(roles) ? roles : [roles]
  // Flatten any nested role arrays
  const normalizedRoles = user.roles.flat?.() ?? user.roles

  return normalizedRoles.some((r) => rolesToCheck.includes(r as UserRole))
}

// Returns true if user has a specific permission
export function hasPermission(user: User | null | undefined, permission: string) {
  return user?.permissions?.includes(permission) ?? false
}

// Returns the display name(s) of the user's roles
export function getRoleName(user: User | null | undefined): string {
  if (!user || !user.roles || user.roles.length === 0) return 'Unknown'
  return user.roles.map((r) => roleNames[r] || r).join(', ')
}

// Returns the display name(s) of the user's roles
export function getRoleLabel(role: UserRole | null | undefined): string {
  if (!role) return 'Unknown'
  return roleNames[role] || role
}
